import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { useAuth } from '../auth/AuthContext'
import config from '../config'

export default function Login() {
  const navigate = useNavigate()
  const {
    login,
    isAuthenticated,
    mfaRequired,
    mfaSetupRequired,
    mfaSetupData,
    verifyMfaCode,
    completeMfaSetup,
    cancelMfa,
  } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Redirect if already authenticated or auth is disabled
  if (isAuthenticated || !config.features.authEnabled) {
    navigate('/', { replace: true })
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login(email, password)
      // If no MFA required, we're logged in and will redirect
      if (!mfaRequired && !mfaSetupRequired) {
        navigate('/')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await verifyMfaCode(mfaCode)
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code')
      setMfaCode('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaSetup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await completeMfaSetup(mfaCode)
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code')
      setMfaCode('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    cancelMfa()
    setMfaCode('')
    setError('')
  }

  // Generate TOTP URI for QR code
  const getTotpUri = () => {
    if (!mfaSetupData) return ''
    const issuer = 'PortfolioTracker'
    return `otpauth://totp/${issuer}:${mfaSetupData.email}?secret=${mfaSetupData.secretCode}&issuer=${issuer}`
  }

  // MFA Setup Screen (first time setup with QR code)
  if (mfaSetupRequired && mfaSetupData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="max-w-md w-full">
          <div className="card">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Set Up Authenticator</h1>
              <p className="text-gray-600 mt-2">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
            </div>

            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white rounded-lg shadow-inner">
                <QRCodeSVG value={getTotpUri()} size={200} />
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-500 text-center mb-2">
                Or enter this code manually:
              </p>
              <code className="block text-center bg-gray-100 p-2 rounded font-mono text-sm break-all">
                {mfaSetupData.secretCode}
              </code>
            </div>

            <form onSubmit={handleMfaSetup} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter code from authenticator
                </label>
                <input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  className="input text-center text-2xl tracking-widest"
                  placeholder="000000"
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || mfaCode.length !== 6}
                className="w-full btn btn-primary"
              >
                {isLoading ? 'Verifying...' : 'Complete Setup'}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="w-full btn btn-secondary"
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // MFA Verification Screen (returning user with MFA enabled)
  if (mfaRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="max-w-md w-full">
          <div className="card">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h1>
              <p className="text-gray-600 mt-2">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            <form onSubmit={handleMfaVerify} className="space-y-6">
              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  className="input text-center text-2xl tracking-widest"
                  placeholder="000000"
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || mfaCode.length !== 6}
                className="w-full btn btn-primary"
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="w-full btn btn-secondary"
              >
                Back to Login
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // Regular Login Screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Portfolio Tracker</h1>
            <p className="text-gray-600 mt-2">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
